import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { ButtonV2, H1, P, Spinner } from 'shared';
import institutionIcon from 'utility-icons/institution.svg';
import { toastSuccess } from 'utils';
import { FlowActions } from '../../components';
import { BANK_ACCOUNT_PARAM, CHANGE_BANK_ACCOUNT_PARAM } from '../../constants';
import { useAuthorizationToken, useEffectiveBankAccountId, useFlexFinalizeData, useFlowPath, } from '../../hooks';
import { getPathWithToken, getPostBankConnectionRoute } from '../../utils';
import { ChooseBankAccountModal, ConnectedBankAccountRow, LinkDifferentAccountButton, } from '../components';
import { useActiveBankAccounts } from '../hooks';
import { getDefaultBankAccount } from '../utils';
const ACCOUNT_ON_FILE_COPY = 'Use the account already on file or connect a different one.';
const DOWN_PAYMENT_COPY = 'Your down payment (due today) and future payments will come from the account you choose.';
function SelectBankAccount() {
    const history = useHistory();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const authorizationToken = useAuthorizationToken();
    const flowPath = useFlowPath(authorizationToken);
    const { lenderAccountIdentifier, finalizeData } = useFlexFinalizeData(authorizationToken);
    const { bankAccounts, isResolved, hasLoadError, refetch } = useActiveBankAccounts(lenderAccountIdentifier);
    const effectiveBankAccountId = useEffectiveBankAccountId(finalizeData);
    useEffect(() => {
        if (!finalizeData || !isResolved || bankAccounts.length === 0)
            return;
        const isUsable = bankAccounts.some((account) => account.id === effectiveBankAccountId);
        if (isUsable)
            return;
        history.replace(flowPath('/autopay', {
            [BANK_ACCOUNT_PARAM]: String(getDefaultBankAccount(bankAccounts).id),
        }));
    }, [
        finalizeData,
        isResolved,
        bankAccounts,
        effectiveBankAccountId,
        flowPath,
        history,
    ]);
    const handleLinkDifferentAccount = () => history.push(flowPath('/autopay/connect', { [CHANGE_BANK_ACCOUNT_PARAM]: '1' }));
    const handleChange = () => {
        if (bankAccounts.length > 1) {
            setIsModalOpen(true);
            return;
        }
        handleLinkDifferentAccount();
    };
    if (!lenderAccountIdentifier || !finalizeData || !isResolved) {
        return (_jsx("div", { className: "text-center", children: _jsx(Spinner, {}) }));
    }
    if (hasLoadError) {
        return (_jsx("div", { className: "connect-bank-account", children: _jsxs("div", { className: "connect-bank-account__card text-center", children: [_jsx(P, { variant: "body-large", className: "mar-b-0", children: "We couldn't load your bank accounts. Please try again." }), _jsx(ButtonV2, { type: "button", variant: "filled", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: () => refetch(), children: "Try Again" })] }) }));
    }
    if (bankAccounts.length === 0) {
        return (_jsx(Redirect, { to: getPathWithToken('/autopay/connect', authorizationToken) }));
    }
    const selectedAccount = bankAccounts.find((account) => account.id === effectiveBankAccountId) ??
        getDefaultBankAccount(bankAccounts);
    const handleSelect = (bankAccountId) => {
        setIsModalOpen(false);
        history.replace(flowPath('/autopay', { [BANK_ACCOUNT_PARAM]: String(bankAccountId) }));
        if (bankAccountId !== selectedAccount.id) {
            toastSuccess('Bank Account Updated');
        }
    };
    return (_jsxs("div", { className: "connect-bank-account", children: [_jsx(H1, { variant: "headline-large", children: "Connect Bank Account" }), _jsxs("div", { className: "connect-bank-account__card", children: [_jsx("div", { className: "connect-bank-account__illustration", children: _jsx("img", { src: institutionIcon, alt: "" }) }), _jsx("div", { className: "text-center", children: _jsx(P, { variant: "body-large", className: "mar-b-0", children: finalizeData.downPaymentRequired
                                ? `${ACCOUNT_ON_FILE_COPY} ${DOWN_PAYMENT_COPY}`
                                : ACCOUNT_ON_FILE_COPY }) }), _jsxs("div", { className: "select-bank-account__accounts", children: [_jsx(ConnectedBankAccountRow, { account: selectedAccount, onChange: handleChange }), _jsx(LinkDifferentAccountButton, { onClick: handleLinkDifferentAccount })] })] }), _jsx(FlowActions, { secondary: _jsx(ButtonV2, { type: "button", variant: "ghost", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: () => history.push(flowPath('/next-steps')), children: "Back" }), children: _jsx(ButtonV2, { type: "button", variant: "filled", color: "orange-flex", size: "medium", hugText: true, overwriteCasing: true, onClick: () => history.push(flowPath(getPostBankConnectionRoute(finalizeData))), children: "Continue" }) }), _jsx(ChooseBankAccountModal, { isOpen: isModalOpen, setIsOpen: setIsModalOpen, bankAccounts: bankAccounts, selectedBankAccountId: selectedAccount.id, onConfirm: handleSelect, onLinkDifferentAccount: handleLinkDifferentAccount })] }));
}
export default SelectBankAccount;
